// Accounts slice for Redux Toolkit
//
// This is the single shared cache for accounts data: TransactionForm (invoice/
// credit-note account picker) and ManageAccounts (CRUD) both read from and
// write to this store instead of independently fetching/holding their own copy.

import { createSlice } from '@reduxjs/toolkit';
import { accountsAPI } from '../api/accounts';

const initialState = {
  accounts: [],
  activeAccounts: [],
  loading: false,
  error: null
};

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    fetchAccountsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAccountsSuccess: (state, action) => {
      state.loading = false;
      state.accounts = action.payload;
    },
    fetchActiveAccountsSuccess: (state, action) => {
      state.loading = false;
      state.activeAccounts = action.payload;
    },
    fetchAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    addAccount: (state, action) => {
      state.accounts.push(action.payload);
      if (action.payload.stop === 0) {
        state.activeAccounts.push(action.payload);
      }
    },
    updateAccount: (state, action) => {
      const index = state.accounts.findIndex(acc => acc.id === action.payload.id);
      if (index !== -1) {
        state.accounts[index] = action.payload;
      }

      // Keep the activeAccounts cache (used by the invoice/credit-note
      // account picker) consistent with any stop-status change here too,
      // instead of requiring a separate refetch.
      const activeIndex = state.activeAccounts.findIndex(acc => acc.id === action.payload.id);
      if (action.payload.stop === 0) {
        if (activeIndex !== -1) {
          state.activeAccounts[activeIndex] = action.payload;
        } else {
          state.activeAccounts.push(action.payload);
        }
      } else if (activeIndex !== -1) {
        state.activeAccounts.splice(activeIndex, 1);
      }
    },
    removeAccount: (state, action) => {
      state.accounts = state.accounts.filter(acc => acc.id !== action.payload);
      state.activeAccounts = state.activeAccounts.filter(acc => acc.id !== action.payload);
    }
  }
});

export const {
  fetchAccountsStart,
  fetchAccountsSuccess,
  fetchActiveAccountsSuccess,
  fetchAccountsFailure,
  addAccount,
  updateAccount,
  removeAccount
} = accountsSlice.actions;

export default accountsSlice.reducer;

// Selectors
export const selectAccounts = (state) => state.accounts.accounts;
export const selectActiveAccounts = (state) => state.accounts.activeAccounts;
export const selectAccountsLoading = (state) => state.accounts.loading;

// Thunks - fetch/mutate via the API and keep the shared cache in sync so
// every consumer (form dropdowns, the management page) sees the same data
// without each one issuing its own redundant network request.

export const fetchAccounts = () => async (dispatch) => {
  dispatch(fetchAccountsStart());
  try {
    const response = await accountsAPI.getAll();
    dispatch(fetchAccountsSuccess(response.data || []));
  } catch (error) {
    dispatch(fetchAccountsFailure(error.message));
    throw error;
  }
};

export const fetchActiveAccounts = () => async (dispatch) => {
  dispatch(fetchAccountsStart());
  try {
    const response = await accountsAPI.getActive();
    dispatch(fetchActiveAccountsSuccess(response.data || []));
  } catch (error) {
    dispatch(fetchAccountsFailure(error.message));
    throw error;
  }
};

export const createAccountThunk = (data) => async (dispatch) => {
  const response = await accountsAPI.create(data);
  dispatch(addAccount(response.data));
  return response.data;
};

export const updateAccountThunk = (id, data) => async (dispatch) => {
  const response = await accountsAPI.update(id, data);
  dispatch(updateAccount(response.data));
  return response.data;
};

export const deleteAccountThunk = (id) => async (dispatch) => {
  await accountsAPI.delete(id);
  dispatch(removeAccount(id));
};
